<!-- StreetMap component for displaying a map with a search bar -->
<!--Copy right - © 2024 tecera gmbh-->

<script>
    import {
        selected_device,
        map_center,
        backup_map_center,
        all_water_meter_boxes
    } from "../stores.js";
    import L from "leaflet";
    import { onDestroy, onMount } from "svelte";
    import MapSearchBar from "./MapSearchBar.svelte";
    import { find_main_meter } from "../scripts/utils.js";

    export let devices; // the device list displayed on the map
    export let alarms_triggered; // the alarm list belonging to the devices
    export let multiple_devices;

    const MARKER_RADIUS = 14;
    const DEFAULT_ZOOM = 16;
    let map; // the leaflet map
    let searchbar_div = null; // the outer div for the search bar
    let isSearchBarEnabled = false; //  gets toggled in MapSearchBar component
    let mapElement = "streetmap"; // html component of the map
    let marker_layer; // extra layer for the map. can be used to remove all markers at once
    let are_markers_set = false;

    onMount(() => {
        createMap();
        toolbar.addTo(map);
    });

    onDestroy(() => {
        if (marker_layer) {
            map.removeLayer(marker_layer); // Remove the marker layer from the map
        }
    });

    // creating markers after devices are loaded
    $: if (map && $devices && $devices.length > 0) {
        if (!are_markers_set) {
            createMarkers();
            are_markers_set = true;
        }
    }

    $: {
        if ($alarms_triggered) {
            refresh_map();
        }
    }

    // changing location in case the center gets changed anywhere else in the code
    $: {
        if (map && $map_center) {
            changeLocation($map_center[0], $map_center[1]);
        }
    }

    $: {
        if ($devices) {
            if (are_markers_set) {
                map.removeLayer(marker_layer);
                createMarkers();
            }
        }
    }

    $: {
        if ($all_water_meter_boxes) {
            if (are_markers_set) {
                map.removeLayer(marker_layer);
                createMarkers();
            }
        }
    }


    function refresh_map() {
        if (marker_layer) {
            map.removeLayer(marker_layer);
            createMarkers();
        }
    }

    function createMap() {
        let center =
            $map_center && $map_center[0] != null && $map_center[1] != null
                ? $map_center
                : $backup_map_center;
        map = L.map(mapElement, {
            center: center,
            zoom: DEFAULT_ZOOM,
        });

        // Leaflet Map mit OpenStreetMap Contributors einbinden
        L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
            attribution:
                '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        }).addTo(map);

        // Leaflet prefix ausblenden
        map.attributionControl.setPrefix(
            '<a href="https://www.leafletjs.com">Leaflet</a>',
        );

        map.on("zoomed", function () {
            // Falls Sensormarker auf Map beim zoomem doch noch verkleinert / vergrößert werden sollen
            // Funktion wird pro Scroll ausgeführt
        });
    }

    // creating the search bar
    let toolbar = L.control({ position: "topleft" });
    let search_bar;
    toolbar.onAdd = (map) => {
        searchbar_div = L.DomUtil.create("div");
        searchbar_div.setAttribute("id", "search_on_map");
        searchbar_div.classList.add("search_on_map", "search_on_map_unfocus");
        search_bar = new MapSearchBar({
            target: searchbar_div,
            props: {},
        });
        search_bar.$on("toggleSearch", ({ detail }) => {
            isSearchBarEnabled = detail;
            if (isSearchBarEnabled) {
                searchbar_div.classList.remove("search_on_map_unfocus");
                searchbar_div.classList.add("search_on_map_focus");
            } else {
                searchbar_div.classList.remove("search_on_map_focus");
                searchbar_div.classList.add("search_on_map_unfocus");
            }
        });
        search_bar.$on("search", ({ detail }) => {
            get_coords(detail).then((center) => {
                map_center.set(center);
            });
        });

        return searchbar_div;
    };

    /**
     *
     * Funktion ermittelt die Koordinaten der genannten Addresse mit Hilfe von Nominatim von OSM
     * Falls Koordinaten nicht ermittelt werden können, werden null Werte zurückgegeben
     *
     * @param {String} address
     */
    async function get_coords(address) {
        let url = `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${address}`;
        try {
            let response = await fetch(url);
            if (!response.ok) {
                throw new Error(`Error fetching coordinates`);
            }
            let data = await response.json();
            return [data[0].lat, data[0].lon];
        } catch (error) {
            return [null, null];
        }
    }

    /**
     *
     * Funktion setzt Kartenmittelpunkt auf die neuen angegebenen Koordinaten, falls diese nicht null sind
     *
     * @param {float} Lat
     * @param {float} Lng
     */
    function changeLocation(Lat, Lng) {
        if (Lat == null || Lng == null) {
            return;
        }
        map.panTo(new L.LatLng(Lat, Lng));
    }

    // Creating markers on the map, depending on the state of the device
    function createMarkers() {
        marker_layer = L.layerGroup();
        try {
            let device_list = [...$devices,...$all_water_meter_boxes]; //HACK: WILL NOT WORK IF BOX API TOO LATE
            //le.log("device_list", device_list); // BUG: runs 3 times when changing back from table view. might be flawed reactivity from multiple Stores
            for (let device of device_list) {
                let parent_object = null;
                if (multiple_devices && !device.box_id) {
                    parent_object = device;
                    device = find_main_meter(device);
                }

                if (device.latitude == null || device.longitude == null) {
                    continue;
                }
                let m = createMarker(device, parent_object);
                if (find_related_alarm(device, parent_object)) {
                    marker_layer.addLayer(m[1]);
                }
                marker_layer.addLayer(m[0]);
            }
            marker_layer.addTo(map);
        } catch (error) {
            //TODO: implement
        }
    }


    // this function has the potential to be very slow with alot of data. maybe change it ?
    function find_related_alarm(device, parent_object) {
        let alarm;
        if (multiple_devices) { // Fallunterscheidung
            alarm = $alarms_triggered.find((alarm_triggered) => {
                if (alarm_triggered.compound_water_meter == null) {
                    return alarm_triggered.device_config.id === device.id;
                }

                // Ohne Parent Object handelt es sich in der Regel im eine Box
                // Diese hat keine Alarme
                if (parent_object == null){
                    return false;
                }

                return (
                    alarm_triggered.compound_water_meter.id === parent_object.id
                );
            });
        } else {
            alarm = $alarms_triggered.find((alarm_triggered) => {
                return alarm_triggered.device_config.id === device.id;
            });
        }

        if (alarm) {
            return true;
        }
        return false;
    }

    function createMarker(device, parent_object) {
        let colour = device.active === 1 ? "green" : "gray";
        device.box_id? colour = "purple": null;

        if (find_related_alarm(device, parent_object)) {
            colour = "red";
        }

        let alarmClass = "alarm";
        // the marker
        var html =
            '<svg class="map_marker" viewBox="0 0 ' +
            MARKER_RADIUS * 2 +
            " " +
            MARKER_RADIUS * 2 +
            '" height="' +
            MARKER_RADIUS * 2 +
            '" width="' +
            MARKER_RADIUS * 2 +
            '">' +
            '<circle cx="' +
            MARKER_RADIUS +
            '" cy="' +
            MARKER_RADIUS +
            '" r="' +
            (MARKER_RADIUS - 1) +
            '"fill="' +
            colour +
            '" stroke="#5a5a5a" stroke-width="2" class="map_marker_circle"/>' +
            "</svg>";

            var html_rect =
                '<svg class="map_marker" viewBox="0 0 ' +
                MARKER_RADIUS * 2 +
                " " +
                MARKER_RADIUS * 2 +
                '" height="' +
                MARKER_RADIUS * 2 +
                '" width="' +
                MARKER_RADIUS * 2 +
                '">' +
                '<rect x="0" y="0" width="' +
                MARKER_RADIUS * 2 +
                '" height="' +
                MARKER_RADIUS * 2 +
                '" fill="' +
                colour +
                '" stroke="#5a5a5a" stroke-width="2" class="map_marker_square"/>' +
                "</svg>";

        // underlying marker for the alarm animation
        var html_alarm =
            '<svg class="map_marker_outer" viewBox="0 0 ' +
            MARKER_RADIUS * 8 +
            " " +
            MARKER_RADIUS * 8 +
            '" height="' +
            MARKER_RADIUS * 8 +
            '" width="' +
            MARKER_RADIUS * 8 +
            '">' +
            "<style>" +
            "@keyframes alarm {" +
            "    0% { r: 14; stroke-opacity: 1; }" +
            "    100% { r: 42; stroke-opacity: 0; }" +
            "}" +
            ".map_marker_alarm.alarm {" +
            "    animation: alarm 2s infinite;" +
            "    stroke: red;" +
            "    stroke-width: 4;" +
            "    fill: none;" +
            "    pointer-events: none;" +
            "}" +
            ".map_marker_alarm.hidden {" +
            "    pointer-events: none;" +
            "    display: none;" +
            "}" +
            "</style>" +
            '<circle cx="' +
            MARKER_RADIUS * 4 +
            '" cy="' +
            MARKER_RADIUS * 4 +
            '" r="' +
            MARKER_RADIUS +
            '"class="map_marker_alarm ' +
            alarmClass +
            '"/>' +
            "</svg>";

        let icon = L.divIcon({
            html: device.box_id? html_rect :html,
            className: "map_circle",
            iconAnchor: [MARKER_RADIUS, MARKER_RADIUS],
        });
        let icon_alarm = L.divIcon({
            html: html_alarm,
            className: "map_circle_alarm",
            iconAnchor: [MARKER_RADIUS * 4, MARKER_RADIUS * 4],
        });

        let on_click_object = parent_object ? parent_object : device;
        let marker = L.marker([device.latitude, device.longitude], {
            icon,
            on_click_object,
        }).bindTooltip(device.name, {
            offset: L.point(0, 20),
            direction: "bottom",
            opacity: 0.9,
        });
        let marker_alarm = L.marker([device.latitude, device.longitude], {
            icon: icon_alarm,
        });
      

        marker.on("click", (e) => {
            let marker = e.target;
            let device_obj = marker.options.on_click_object;
            $map_center=[device.latitude, device.longitude];
            $selected_device==device_obj ? null: $selected_device =device_obj;
        });

        if (device.box_id){
            marker.setZIndexOffset(100);
            marker_alarm.setZIndexOffset(100);
        } else {
            marker.setZIndexOffset(100000);
            marker_alarm.setZIndexOffset(100000);
        }

        return [marker, marker_alarm];
        
    }
</script>

<div class="display_box streetmap__outer">
    <div id="streetmap" class="streetmap__inner"></div>
</div>
